import { trigger, transition, style, animate } from '@angular/animations';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString, ERoleString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { ESolicitacaoConsultaStatus, ISolicitacaoConsultaFiltros } from '../../consultas/data/consultas';
import { ConsultasService } from '../../consultas/services/consultas.service';
import { ISidebarMenuGroup, MENU_GROUPS, ISidebarMenuItem } from './sidebar.models';
import { AdminOliveService } from 'src/app/shared/services/admin-olive.service';
import { ThemeService } from 'src/app/pages/settings-dashboard/services/theme.service';
import { HistoricoPaginasService } from 'src/app/shared/services/historico-paginas.service';
import { AuthService } from 'src/app/auth/services/auth.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class SidebarComponent implements OnInit, OnDestroy {
  ERoleString = ERoleString;
  ERoleAdminString = ERoleAdminString;

  hasConsultasPendentes$!: Observable<boolean>;
  expandedGroups = new Set<number>();

  menuGroups: ISidebarMenuGroup[] = MENU_GROUPS;
  // resolvido pelo account-api no ngOnInit; até chegar, itens internos ficam ocultos
  ehAdminOlive = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly consultasService: ConsultasService,
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly historicoPaginasService: HistoricoPaginasService,
    private readonly adminOliveService: AdminOliveService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.hasConsultasPendentes$ = this.consultasService.hasConsultasPendentes$.asObservable();

    this.adminOliveService
      .garantirResolvido()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ehAdminOlive) => (this.ehAdminOlive = ehAdminOlive));

    if (
      this.sessionService.getSession().role === ERoleAdminString.Doctor ||
      this.sessionService.getSession().userdata.includes('appointments.list')
    ) {
      this.carregarConsultas();
    }

    this.expandActiveGroup();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.expandActiveGroup();
        this.historicoPaginasService.registrarPagina(this.router.url);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleGroup(index: number): void {
    if (this.expandedGroups.has(index)) {
      this.expandedGroups.delete(index);
    } else {
      this.expandedGroups.add(index);
    }
  }

  isGroupExpanded(index: number): boolean {
    return this.expandedGroups.has(index);
  }

  carregarConsultas(): void {
    const filtros: ISolicitacaoConsultaFiltros = {
      orderBy: EOrdenacao.MaisNovos,
      pageNumber: 0,
      pageSize: 1,
      doctorId: this.sessionService.getSession()!.sid!,
      periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
      periodEnd: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString(),
      status: Number(ESolicitacaoConsultaStatus.pendenteAprovacao)
    };

    this.consultasService.listarTodasSolicitacoesConsultas(filtros).subscribe((res) => {});
  }

  logout(): void {
    sessionStorage.removeItem('isAvailable');
    this.themeService.clearTheme();
    // Limpa a sessão e vai ao login; no modo Keycloak encerra também a sessão de SSO.
    this.authService.logout();
  }

  isGroupVisible(group: ISidebarMenuGroup): boolean {
    return group.items.some((item) => this.isItemVisible(item));
  }

  isItemVisible(item: ISidebarMenuItem): boolean {
    if (!item.permissions || item.permissions.length === 0) return false;

    const session = this.sessionService.getSession();

    // no logout a sessão é limpa antes do sidebar sair da tela, e o change detection
    // ainda reavalia os itens uma última vez
    if (!session) return false;

    const userPermissions = session.userdata || [];

    // Item restrito: exige o flag "Admin Oliv-e" do perfil e todas as permissões listadas.
    // A role não entra na conta — atendente e gestor são ambos Master, então ela não separa nada.
    if (item.restrito) {
      return this.ehAdminOlive && item.permissions.every((permission) => userPermissions.includes(permission));
    }

    return item.permissions.some((permission) => permission === session.role || userPermissions.includes(permission));
  }

  private expandActiveGroup(): void {
    const currentUrl = this.router.url;
    const activeIndex = this.menuGroups.findIndex((group) =>
      group.items.some((item) => currentUrl.startsWith('/dash/' + item.route))
    );

    if (activeIndex !== -1) {
      this.expandedGroups.add(activeIndex);
    }
  }
}
